import React from "react";
import { Link, useLocation } from "react-router-dom";
import { CODLogoMark } from "./Logo";
import { Button } from "../ui/button";
import { useAuth } from "../../lib/auth";
import { portalForUser } from "../../lib/roles";
import PublicChatWidget from "./PublicChatWidget";

/** Site-wide "launching in phases" notice. Sits above the primary nav so
 *  every public page communicates the current phase without hiding the
 *  broader vision. Dismissible per browser session. */
const PhaseBanner = () => {
  const [dismissed, setDismissed] = React.useState(() => {
    try { return sessionStorage.getItem("cod-phase-banner") === "hidden"; }
    catch { return false; }
  });
  if (dismissed) return null;
  const hide = () => {
    try { sessionStorage.setItem("cod-phase-banner", "hidden"); } catch {}
    setDismissed(true);
  };
  return (
    <div className="w-full border-b border-slate-200"
         style={{ background: "linear-gradient(90deg,#0B1E3D 0%,#14366B 100%)" }}
         data-testid="site-phase-banner" role="status">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center gap-3 text-white text-xs sm:text-sm">
        <span className="inline-flex items-center gap-1.5 shrink-0">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#C41230] animate-pulse"/>
          <span className="font-semibold uppercase tracking-wider">Now Available</span>
        </span>
        <span className="hidden sm:inline text-white/70">·</span>
        <span className="flex-1 leading-snug">
          Chicago Opportunity Desk is launching in phases. <strong>Employment and staffing services are available now.</strong> Additional workforce, certification, training, business, and community services are being introduced as our network and capacity expand.
        </span>
        <button onClick={hide} aria-label="Dismiss notice"
                data-testid="site-phase-banner-dismiss"
                className="shrink-0 text-white/80 hover:text-white text-lg leading-none px-1">×</button>
      </div>
    </div>
  );
};

const PUBLIC_NAV = [
  { to: "/", label: "Home", tid: "pub-nav-home" },
  { to: "/services", label: "Services", tid: "pub-nav-services" },
  { to: "/marketplace", label: "Marketplace", tid: "pub-nav-marketplace" },
  { to: "/pathways", label: "Pathways", tid: "pub-nav-pathways" },
  { to: "/employers", label: "Employers", tid: "pub-nav-employers" },
  { to: "/partners", label: "Partners", tid: "pub-nav-partners" },
  { to: "/opportunities", label: "Opportunities", tid: "pub-nav-opportunities" },
  { to: "/about", label: "About COD", tid: "pub-nav-about" },
];

export const PublicHeader = () => {
  const { user } = useAuth();
  const loc = useLocation();
  return (
    <>
      {/* Site-wide phased-launch notice — dismissible per session */}
      <PhaseBanner/>
      <div className="cod-stripe"/>
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" aria-label="Chicago Opportunity Desk — Home" data-testid="header-brand" className="flex items-center">
            <CODLogoMark size={56} onLight/>
          </Link>
          <nav className="hidden lg:flex items-center gap-1">
            {PUBLIC_NAV.map(n => {
              const active = n.to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(n.to);
              return (
                <Link key={n.to} to={n.to} data-testid={n.tid}
                  className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${active ? "text-[var(--cod-navy)] border-[var(--cod-red)]" : "text-slate-700 border-transparent hover:text-[var(--cod-navy)] hover:border-slate-200"}`}>
                  {n.label}
                </Link>
              );
            })}
          </nav>
          <div className="flex items-center gap-2">
            {user ? (
              <Link to={portalForUser(user)}>
                <Button className="bg-[var(--cod-navy)] hover:bg-[var(--cod-navy-deep)] text-white" data-testid="header-my-portal">My Workspace</Button>
              </Link>
            ) : (
              <>
                <Link to="/signin"><Button variant="ghost" data-testid="header-signin" className="text-slate-700">Sign In</Button></Link>
                <Link to="/start"><Button className="bg-[var(--cod-red)] hover:bg-[#A40D26] text-white" data-testid="header-create-account">Create Account</Button></Link>
              </>
            )}
          </div>
        </div>
        <div className="lg:hidden border-t border-slate-200 overflow-x-auto scrollbar-thin">
          <div className="flex gap-1 px-3 py-2">
            {PUBLIC_NAV.map(n => {
              const active = n.to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(n.to);
              return (
                <Link key={n.to} to={n.to} data-testid={`m-${n.tid}`}
                  className={`whitespace-nowrap px-3 py-1.5 rounded-md text-xs font-medium ${active ? "bg-[var(--cod-navy)] text-white" : "bg-slate-100 text-slate-700"}`}>
                  {n.label}
                </Link>
              );
            })}
          </div>
        </div>
      </header>
      <PublicChatWidget/>
    </>
  );
};
